import { useNavigate } from 'react-router-dom';

interface NextStepCardProps {
  startupId?: string;
  pipelineId?: string;
  pipelineStatus?: string;
  pipelineComplete?: boolean;
  hasScenarios?: boolean;
  betaUserCount?: number;
  feedbackCount?: number;
  nps?: number | null;
  validationTasksDone?: number;
}

interface NextStep {
  icon: string;
  title: string;
  description: string;
  cta: string;
  href: string;
  color: string;
}

function computeNextStep(props: NextStepCardProps): NextStep {
  const { pipelineId, pipelineStatus, startupId, hasScenarios, betaUserCount, feedbackCount, validationTasksDone } = props;

  // No pipeline started
  if (!pipelineId || pipelineStatus === undefined) {
    return {
      icon: '⚡',
      title: 'Validate your idea',
      description: 'Run the 5-step AI pipeline to validate your idea with real market data.',
      cta: 'Start Pipeline →',
      href: '/pipeline',
      color: '#7c6ff7',
    };
  }

  // Pipeline active / in progress
  if (pipelineStatus === 'active') {
    return {
      icon: '⚡',
      title: 'Continue your pipeline',
      description: 'Complete your validation to unlock your MVP Blueprint — the real one, built on your actual results.',
      cta: 'Continue →',
      href: `/pipeline/${pipelineId}`,
      color: '#7c6ff7',
    };
  }

  // Pipeline complete — check validation plan tasks
  if (pipelineStatus === 'completed' && startupId) {
    const tasksDone = validationTasksDone ?? 0;

    // Start validation plan if no tasks done
    if (tasksDone === 0) {
      return {
        icon: '🧪',
        title: 'Start your 14-day validation plan',
        description: 'Your pipeline is complete! Now execute your personalized 14-day validation plan to test your idea with real users.',
        cta: 'Open Validation Plan →',
        href: `/startups/${startupId}/dashboard`,
        color: '#f97316',
      };
    }

    // Continue validation plan if partially done
    if (tasksDone > 0 && tasksDone < 14) {
      return {
        icon: '🧪',
        title: `Continue validation plan (${tasksDone}/14 tasks)`,
        description: `You're making progress! Complete your remaining ${14 - tasksDone} validation tasks to build evidence for your MVP.`,
        cta: 'Continue Plan →',
        href: `/startups/${startupId}/dashboard`,
        color: '#f97316',
      };
    }

    // No beta users yet
    if (!betaUserCount || betaUserCount === 0) {
      return {
        icon: '👥',
        title: 'Add your first beta tester',
        description: 'Real user feedback is more valuable than any analysis. Start recruiting beta users.',
        cta: 'Go to Beta Testing →',
        href: `/startups/${startupId}/beta`,
        color: '#00e676',
      };
    }

    // No financials
    if (!hasScenarios) {
      return {
        icon: '📊',
        title: 'Model your financials',
        description: 'Your idea is validated — now model revenue scenarios and find your break-even month.',
        cta: 'Open Financials →',
        href: `/startups/${startupId}/financials`,
        color: '#00d4ff',
      };
    }

    // Has beta users but no feedback
    if (betaUserCount > 0 && (!feedbackCount || feedbackCount === 0)) {
      return {
        icon: '💬',
        title: 'Collect feedback',
        description: `You have ${betaUserCount} beta user${betaUserCount !== 1 ? 's' : ''}. Send them the feedback form to gather NPS and feature requests.`,
        cta: 'View Beta →',
        href: `/startups/${startupId}/beta`,
        color: '#ffb800',
      };
    }

    // Has feedback — go to dashboard
    if (feedbackCount && feedbackCount > 0) {
      return {
        icon: '📈',
        title: 'Review your metrics',
        description: `You have ${feedbackCount} feedback response${feedbackCount !== 1 ? 's' : ''}. Check your dashboard to analyze NPS and traction.`,
        cta: 'Open Dashboard →',
        href: `/startups/${startupId}/dashboard`,
        color: '#a855f7',
      };
    }
  }

  // Default fallback
  return {
    icon: '⚡',
    title: 'Complete your pipeline',
    description: 'Validate your startup idea with AI-powered analysis.',
    cta: 'Go to Pipeline →',
    href: '/pipeline',
    color: '#7c6ff7',
  };
}

export function NextStepCard(props: NextStepCardProps) {
  const navigate = useNavigate();
  const step = computeNextStep(props);

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(13,13,31,0.95), rgba(18,18,40,0.9))',
      border: `1px solid ${step.color}30`,
      borderRadius: 18, padding: '24px 28px', marginBottom: 28,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 20, flexWrap: 'wrap',
      boxShadow: `0 4px 32px ${step.color}10`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: 1, minWidth: 240 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          background: `${step.color}15`, border: `1px solid ${step.color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
        }}>
          {step.icon}
        </div>
        <div>
          <div style={{ fontSize: 10, color: step.color, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
            Recommended Next Step
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#f0f0ff', marginBottom: 4 }}>{step.title}</h3>
          <p style={{ fontSize: 13, color: '#8888aa', lineHeight: 1.5 }}>{step.description}</p>
        </div>
      </div>
      <button
        onClick={() => navigate(step.href)}
        style={{
          background: `linear-gradient(135deg, ${step.color}, ${step.color}cc)`,
          border: 'none', borderRadius: 12, color: '#fff',
          fontSize: 14, fontWeight: 700, padding: '12px 22px',
          cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        {step.cta}
      </button>
    </div>
  );
}
